import { ExclamationCircleOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, GridApi, ICellRendererParams, RowNode } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { Switch, theme, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'

import { invoiceStatusConfig } from '../../Invoices/types'
import { useFilteredCustomers } from '../hooks'
import { OrderDetailButton } from './common/OrderDetailButton'
import { WarningTag } from './common/WarningTag'
import { useLaunchpadContext } from '../context'
import type { Customer, CustomerOrder } from '../../../../api/useFreight/types/dashboard'
import type { InvoiceStatus } from '../../../../api/useFreight/types'

interface CellRendererProps<T> extends ICellRendererParams {
  data: T
  api: GridApi
  node: RowNode
}

export function CustomerOverview() {
  const { data, triggerDrawer } = useLaunchpadContext()
  const [showErrorsOnly, setShowErrorsOnly] = useState(false)
  const { token } = theme.useToken()
  const rowData = useFilteredCustomers(data?.customers, showErrorsOnly)

  if (!data) {
    return null
  }

  const customerColumnDefs: ColDef<Customer>[] = [
    {
      headerName: '',
      field: 'id',
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        suppressCount: true,
        padding: 0,
      },
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      resizable: false,
      sortable: false,
      suppressMenu: true,
    },
    {
      field: 'warningCounts',
      headerName: '',
      flex: 0.6,
      suppressSizeToFit: true,
      cellRenderer: CustomerWarningCell,
    },
    {
      field: 'name',
      headerName: 'Customer Name',
      cellStyle: {
        fontWeight: 'bold',
      },
      flex: 1.5,
      suppressSizeToFit: true,
    },
    {
      field: 'openAmount',
      headerName: 'Open',
      flex: 1,
      type: 'numericColumn',
      cellRenderer: CustomerOpenAmountCell,
    },
    {
      field: 'sentMTD',
      headerName: 'Sent',
      flex: 1,
      type: 'numericColumn',
      cellRenderer: CustomerSentMTDCell,
    },
  ]

  const orderColumnDefs: ColDef<CustomerOrder>[] = [
    {
      field: 'orderId',
      headerName: '',
      flex: 0.5,
      cellRenderer: (props: CellRendererProps<CustomerOrder>) => (
        <OrderDetailCell {...props} triggerDrawer={triggerDrawer} />
      ),
    },
    {
      field: 'warningCount',
      headerName: '',
      flex: 0.6,
      suppressSizeToFit: true,
      cellRenderer: OrderWarningCell,
    },
    {
      field: 'date',
      headerName: 'Date',
      initialSort: 'desc',

      flex: 1.25,
      suppressSizeToFit: true,
      cellRenderer: OrderDateCell,
    },
    {
      field: 'orderNumber',
      headerName: 'Order',
      flex: 0.75,
      cellRenderer: OrderNumberCell,
    },
    {
      field: 'invoiceNumber',
      headerName: 'Inv. Number',
      flex: 1,
      suppressSizeToFit: true,
      cellRenderer: InvoiceNumberCell,
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 1.25,
      type: 'numericColumn',
      cellStyle: {
        fontWeight: 'bold',
        textAlign: 'right',
      },
      valueFormatter: (params) =>
        params.value.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        }),
    },
  ]

  return (
    <Vertical>
      <Horizontal
        bg='elevated'
        border='all'
        className='p-3'
        justifyContent='space-between'
        flex='0'
        style={{ overflow: 'visible' }}
        verticalCenter
      >
        <Texto category='h5'>Customer Invoicing Overview</Texto>
        <Horizontal verticalCenter gap={8}>
          <Texto category='p2' appearance='hint'>
            Only Errors
          </Texto>
          <Switch
            checked={showErrorsOnly}
            onChange={() => setShowErrorsOnly(!showErrorsOnly)}
            checkedChildren={<ExclamationCircleOutlined />}
            unCheckedChildren={<ExclamationCircleOutlined />}
            style={{
              cursor: 'pointer',
              backgroundColor: showErrorsOnly ? token.colorError : undefined,
            }}
          />
        </Horizontal>
      </Horizontal>

      <Vertical flex={1} className='ag-theme-alpine'>
        <AgGridReact
          columnDefs={customerColumnDefs}
          rowData={rowData}
          masterDetail
          headerHeight={32}
          rowHeight={40}
          detailRowAutoHeight
          detailCellRendererParams={{
            detailGridOptions: {
              context: {
                triggerDrawer,
              },
              columnDefs: orderColumnDefs,
              rowHeight: 30,
              headerHeight: 25,
            },
            getDetailRowData: (params: {
              data: Customer
              successCallback: (orders: CustomerOrder[]) => void
            }) => {
              params.successCallback(params.data.orders)
            },
          }}
        />
      </Vertical>
    </Vertical>
  )
}

// Cell Renderer Components
function CustomerWarningCell({ data }: CellRendererProps<Customer>) {
  return (
    <WarningTag
      errorCount={data.errorCounts}
      warningCount={data.warningCounts}
      tooltipErrorPrefix='orders'
      tooltipWarningPrefix='orders'
    />
  )
}

function CustomerOpenAmountCell({ data }: CellRendererProps<Customer>) {
  if (data.openOrders === 0) {
    return (
      <Texto appearance='hint' style={{ lineHeight: '1.2' }} align='right'>
        N/A
      </Texto>
    )
  }

  return (
    <Vertical style={{ overflow: 'visible' }} verticalCenter>
      <Texto weight='bold' style={{ lineHeight: '1.4' }} align='right'>
        {data.openAmount.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })}
      </Texto>
      <Texto weight='600' category='label' style={{ lineHeight: '1.2' }} align='right'>
        {data.openOrders} Orders
      </Texto>
    </Vertical>
  )
}

function CustomerSentMTDCell({ data }: CellRendererProps<Customer>) {
  if (data.sentOrders === 0) {
    return (
      <Texto appearance='hint' style={{ lineHeight: '1.2' }} align='right'>
        N/A
      </Texto>
    )
  }

  return (
    <Vertical style={{ overflow: 'visible' }} verticalCenter>
      <Texto weight='bold' style={{ lineHeight: '1.4' }} align='right'>
        {data.sentMTD.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })}
      </Texto>
      <Texto weight='600' category='label' style={{ lineHeight: '1.2' }} align='right'>
        {data.sentOrders} Orders
      </Texto>
    </Vertical>
  )
}

function OrderWarningCell({ data }: CellRendererProps<CustomerOrder>) {
  return (
    <WarningTag
      errorCount={data.errorCount}
      warningCount={data.warningCount}
      tooltipErrorPrefix='line items'
      tooltipWarningPrefix='line items'
    />
  )
}

interface OrderDetailCellProps extends CellRendererProps<CustomerOrder> {
  triggerDrawer: (params: {
    visible: boolean
    orderNumber: string
    orders: CustomerOrder[]
    currentIndex: number
  }) => void
}

function OrderDetailCell({ data, api, node, triggerDrawer }: OrderDetailCellProps) {
  const handleClick = () => {
    const orders: CustomerOrder[] = []
    api.forEachNode((node) => {
      orders.push(node.data)
    })
    triggerDrawer({
      visible: true,
      orderNumber: data.orderNumber,
      orders,
      currentIndex: node.rowIndex ?? 0,
    })
  }

  return <OrderDetailButton onClick={handleClick} />
}

function OrderDateCell({ data }: CellRendererProps<CustomerOrder>) {
  return (
    <Horizontal gap={4} style={{ overflow: 'visible' }} verticalCenter>
      <Texto
        style={{ paddingTop: 1 }}
        appearance={
          (invoiceStatusConfig[data.invoiceStatus as InvoiceStatus]?.color || 'default') as
            | 'error'
            | 'success'
            | 'default'
            | 'white'
            | 'primary'
            | 'warning'
            | 'hint'
            | 'light'
            | 'medium'
            | 'secondary'
            | 'optimal'
        }
      >
        <MaterialIcon
          fill
          className='pt-1'
          icon={invoiceStatusConfig[data.invoiceStatus as InvoiceStatus]?.icon || 'help'}
        />
      </Texto>
      <Texto category='label' weight='600'>
        {dayjs(data.date).format('MM/DD/YYYY')}
      </Texto>
    </Horizontal>
  )
}

function OrderNumberCell({ data }: CellRendererProps<CustomerOrder>) {
  return (
    <Vertical verticalCenter style={{ overflow: 'visible' }}>
      <Tooltip title='View order on line items'>
        <GraviButton
          className='p-0'
          appearance='link'
          size='small'
          theme2
          style={{
            textTransform: 'uppercase',
            width: 'fit-content',
            fontSize: '0.9em',
            textDecoration: 'underline',
            color: 'var(--theme-color-2)',
          }}
          onClick={() => {
            window.open(`freight_line_items?order=${data.orderNumber}`, '_blank')
          }}
        >
          {data.orderNumber}
        </GraviButton>
      </Tooltip>
    </Vertical>
  )
}

function InvoiceNumberCell({ data }: CellRendererProps<CustomerOrder>) {
  return (
    <Vertical verticalCenter style={{ overflow: 'visible' }}>
      <Tooltip title='View Invoice'>
        <GraviButton
          className='p-0'
          appearance='link'
          size='small'
          theme2
          style={{
            textTransform: 'uppercase',
            width: 'fit-content',
            fontSize: '0.9em',
            textDecoration: 'underline',
            color: 'var(--theme-color-2)',
          }}
          onClick={() => {
            window.open(`invoices/${data.invoiceGroupId}`, '_blank')
          }}
        >
          {data.invoiceNumber}
        </GraviButton>
      </Tooltip>
    </Vertical>
  )
}
